import React, { useState } from "react"
import { useSelector } from "react-redux"
import { Modal } from "react-bootstrap"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatMoney } from "helpers/helpers"
import AnalyticsFilterBar, { FilterField, FilterSelect, FilterSegment } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildPeriod, buildAnalyticsUrl, localDateString } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const inputStyle = { borderRadius: "10px", minHeight: "38px" }

// Длинные периоды для закупок — в общих пресетах их нет, ставятся как «свой период»
const longRangeOptions = [
	{ id: 90, label: "90 дней" },
	{ id: 180, label: "180 дней" },
	{ id: 365, label: "365 дней" },
]

const directionOptions = [
	{ id: "all", name: "Все изменения" },
	{ id: "increase", name: "Только подорожание" },
	{ id: "decrease", name: "Только снижение" },
	{ id: "unchanged", name: "Без изменений" },
	{ id: "single", name: "Одна закупка" },
]

const purchasesOptions = [
	{ id: 2, label: "2+ закупки" },
	{ id: 1, label: "Все" },
]

// Последние N дней до вчера — как у общих пресетов
function lastDaysRange(days) {
	const end = new Date()
	end.setHours(0, 0, 0, 0)
	end.setDate(end.getDate() - 1)
	const start = new Date(end)
	start.setDate(end.getDate() - (days - 1))
	return { start_date: localDateString(start), end_date: localDateString(end) }
}

function formatDateLabel(value) {
	if (!value) return "—"
	const [year, month, day] = String(value).slice(0, 10).split("-")
	if (!year || !month || !day) return value
	return `${day}.${month}.${year}`
}

function PercentBadge({ value }) {
	const percent = Number(value || 0)
	const isUp = percent > 0
	const isDown = percent < 0
	const color = isUp ? "#d23434" : (isDown ? "#1f9d57" : "#6b7280")
	const background = isUp ? "var(--dark-tint-danger, #fdecec)" : (isDown ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-surface-2, #f1f4f8)")
	const icon = isUp ? "uil-arrow-up" : (isDown ? "uil-arrow-down" : "uil-minus")

	return (
		<span className="d-inline-flex align-items-center gap-1" style={{ color, background, borderRadius: "14px", padding: "4px 9px", fontWeight: 800, fontSize: "12px" }}>
			<i className={`uil ${icon}`}></i>
			{percent > 0 ? "+" : ""}{percent.toFixed(1)}%
		</span>
	)
}

function KpiCard({ title, value, sublabel, icon, color = "#4c6ef0" }) {
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body p-3">
				<div className="d-flex justify-content-between align-items-start">
					<div style={{ ...mutedStyle, textTransform: "uppercase", letterSpacing: "0.4px" }}>{title}</div>
					<div className="d-flex align-items-center justify-content-center" style={{ width: "30px", height: "30px", borderRadius: "10px", background: `${color}18`, color }}>
						<i className={`uil ${icon}`}></i>
					</div>
				</div>
				<h3 className="mb-0 mt-2" style={{ fontSize: "24px", fontWeight: 800, color: "var(--dark-text-strong, #111827)", letterSpacing: "-0.5px" }}>
					{value}
				</h3>
				{sublabel && <div style={{ ...mutedStyle, marginTop: "2px" }}>{sublabel}</div>}
			</div>
		</div>
	)
}

function MoneyCell({ value }) {
	const number = Number(value || 0)
	const color = number > 0 ? "#d23434" : (number < 0 ? "#1f9d57" : "#6b7280")
	return <span style={{ color, fontWeight: 800 }}>{number > 0 ? "+" : ""}{formatMoney(number)}</span>
}

function TopChangesTable({ title, items, emptyText }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card h-100" style={cardStyle}>
			<div className="card-body">
				<h5 className="mb-3">{title}</h5>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Поставщик</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Было</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Стало</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Изменение</th>
							</tr>
						</thead>
						<tbody>
							{items.length === 0 && (
								<tr><td colSpan="5" className="text-muted text-center py-4">{emptyText}</td></tr>
							)}
							{items.map((item, index) => {
								const borderBottom = index === items.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={`${item.product_id}-${item.supplier_id || 0}`}>
										<td className="py-3" style={{ borderBottom, fontWeight: 700 }}>{item.product_name}</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{item.supplier_name}</td>
										<td className="py-3 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.first_price)}</td>
										<td className="py-3 text-end text-nowrap" style={{ borderBottom }}>{formatMoney(item.last_price)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}><PercentBadge value={item.delta_percent} /></td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function SuppliersTable({ suppliers }) {
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }
	return (
		<div className="card mb-3" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center mb-2">
					<h5 className="mb-0">Поставщики</h5>
					<span style={mutedStyle}>Кто сколько взял сверх первой цены периода</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Поставщик</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Товаров</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Подорожало</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Подешевело</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Среднее изменение</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Переплата</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Итог к первой цене</th>
							</tr>
						</thead>
						<tbody>
							{suppliers.length === 0 && (
								<tr><td colSpan="7" className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{suppliers.map((supplier, index) => {
								const borderBottom = index === suppliers.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								return (
									<tr key={supplier.supplier_id || index}>
										<td className="py-3" style={{ borderBottom, fontWeight: 700 }}>{supplier.supplier_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(supplier.items_count, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(supplier.increased_count, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(supplier.decreased_count, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											{supplier.compared_count > 0 ? <PercentBadge value={supplier.avg_change_percent} /> : <span style={mutedStyle}>одна закупка</span>}
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}><MoneyCell value={supplier.overpay} /></td>
										<td className="py-3 text-end" style={{ borderBottom }}><MoneyCell value={supplier.net_effect} /></td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	)
}

function ChangesTable({ items, onOpenHistory }) {
	const [visibleCount, setVisibleCount] = useState(30)
	const visible = items.slice(0, visibleCount)
	const remaining = items.length - visible.length
	const headerThStyle = { border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }

	return (
		<div className="card" style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
					<div>
						<h5 className="mb-1">Таблица товаров</h5>
						<div style={mutedStyle}>Клик по строке откроет историю закупочных цен</div>
					</div>
					<span style={{ ...mutedStyle, background: "var(--dark-surface-2, #f1f4f8)", borderRadius: "14px", padding: "4px 10px", color: "var(--dark-text-muted, #5b6472)" }}>
						{formatMoney(items.length, 0)} записей
					</span>
				</div>
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Товар</th>
								<th className="py-3 text-muted fw-semibold" style={headerThStyle}>Поставщик</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Закупок</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Первая цена</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Последняя цена</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Δ %</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Переплата</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Итог к первой цене</th>
								<th className="py-3 text-muted fw-semibold text-end" style={headerThStyle}>Изменение цены</th>
							</tr>
						</thead>
						<tbody>
							{visible.length === 0 && (
								<tr><td colSpan="9" className="text-muted text-center py-4">Нет данных</td></tr>
							)}
							{visible.map((item, index) => {
								const borderBottom = index === visible.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const isSingle = item.change === "single"
								return (
									<tr key={`${item.product_id}-${item.supplier_id || 0}`} className="cursor-pointer" onClick={() => onOpenHistory(item)}>
										<td className="py-3" style={{ borderBottom }}>
											<div style={{ fontWeight: 700 }}>{item.product_name}</div>
											<div style={mutedStyle}>{item.category_name || "без категории"}</div>
										</td>
										<td className="py-3 text-muted" style={{ borderBottom }}>{item.supplier_name}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(item.purchase_count, 0)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{formatMoney(item.first_price)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>{isSingle ? "—" : formatMoney(item.last_price)}</td>
										<td className="py-3 text-end" style={{ borderBottom }}>
											{isSingle ? <span style={mutedStyle}>одна закупка</span> : <PercentBadge value={item.delta_percent} />}
										</td>
										<td className="py-3 text-end" style={{ borderBottom }}><MoneyCell value={item.overpay} /></td>
										<td className="py-3 text-end" style={{ borderBottom }}><MoneyCell value={item.net_effect} /></td>
										<td className="py-3 text-end text-muted text-nowrap" style={{ borderBottom }}>{formatDateLabel(item.last_change_date)}</td>
									</tr>
								)
							})}
						</tbody>
					</table>
				</div>
				{remaining > 0 && (
					<div className="text-center pt-3">
						<button className="btn btn-sm btn-link text-decoration-none" onClick={() => setVisibleCount(visibleCount + 30)}>
							Показать ещё ({remaining}) →
						</button>
					</div>
				)}
			</div>
		</div>
	)
}

function HistoryModal({ item, history, show, onHide }) {
	const chartOptions = {
		chart: { type: "line", toolbar: { show: false } },
		stroke: { curve: "smooth", width: 3 },
		colors: ["#4c6ef0"],
		dataLabels: { enabled: false },
		grid: { borderColor: "#eef2f6" },
		xaxis: { categories: history.map(row => formatDateLabel(row.change_date)) },
		tooltip: { y: { formatter: value => `${formatMoney(value)} сум` } },
	}
	const chartSeries = [{ name: "Закупочная цена", data: history.map(row => Number(row.price || 0)) }]

	return (
		<Modal show={show} onHide={onHide} size="lg" centered>
			<Modal.Header closeButton>
				<Modal.Title>{item?.product_name || "История цены"}</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
				<div className="mb-3" style={mutedStyle}>
					Поставщик: {item?.supplier_name || "—"}
				</div>
				{history.length > 0 && (
					<ReactApexChart options={chartOptions} series={chartSeries} type="line" height={280} />
				)}
				<div className="table-responsive mt-3">
					<table className="table table-bordered table-bordered-custom">
						<thead>
							<tr>
								<th>Дата</th>
								<th>Документ</th>
								<th className="text-end">Цена</th>
								<th className="text-end">Количество</th>
							</tr>
						</thead>
						<tbody>
							{history.length === 0 && (
								<tr><td colSpan="4" className="text-center text-muted">Нет истории</td></tr>
							)}
							{history.map((row, index) => (
								<tr key={index}>
									<td>{formatDateLabel(row.change_date)}</td>
									<td>№ {row.document_id}</td>
									<td className="text-end">{formatMoney(row.price)}</td>
									<td className="text-end">{formatMoney(row.quantity, 3)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Modal.Body>
		</Modal>
	)
}

function PurchasePriceChanges() {
	const reduxAccount = useSelector(state => state.account)
	const [data, setData] = useState(null)
	const [historyItem, setHistoryItem] = useState(null)
	const [history, setHistory] = useState([])

	const filters = useAnalyticsFilters({
		...buildPeriod("30days"),
		pos_id: reduxAccount.pos_id ?? -1,
		wms_id: -1,
		supplier_id: -1,
		category_id: -1,
		direction: "all",
		min_change_percent: "",
		min_purchases: 2,
		search: "",
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["warehouses", "suppliers", "categories"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/purchase-price-changes", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	// История одной пары — лёгким запросом и с тем фильтром, по которому построен отчёт
	async function openHistory(item) {
		setHistoryItem(item)
		setHistory([])
		const url = buildAnalyticsUrl("/analytics/purchase-price-changes", {
			start_date: applied.start_date,
			end_date: applied.end_date,
			pos_id: applied.pos_id,
			wms_id: applied.wms_id,
		}, {
			history: 1,
			product_id: item.product_id,
			supplier_id: item.supplier_id || -1,
		})
		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) setHistory(response.data.history || [])
	}

	function selectLongRange(days) {
		patch({ period: "custom", ...lastDaysRange(days) })
	}

	const activeLongRange = longRangeOptions.find(option => {
		const range = lastDaysRange(option.id)
		return draft.period === "custom" && draft.start_date === range.start_date && draft.end_date === range.end_date
	})

	const summary = data?.summary || {}
	const items = data?.items || []

	return (
		<>
			<AnalyticsFilterBar
				title="Аналитика закупочных цен"
				icon="uil-chart-line"
				subtitle="Что подорожало, что подешевело и сколько это стоило"
				filters={filters}
				presets={["7days", "30days", "this_month", "last_month", "custom"]}
			>
				<FilterField label="Длинный период">
					<FilterSegment options={longRangeOptions} value={activeLongRange?.id} onChange={selectLongRange} />
				</FilterField>
				<FilterField label="Филиал">
					<FilterSelect options={options.poses} allLabel="Все филиалы" value={draft.pos_id} onChange={(id) => patch({ pos_id: id, wms_id: -1, supplier_id: -1 })} />
				</FilterField>
				<FilterField label="Склад">
					<FilterSelect options={options.warehouses} allLabel="Все склады" value={draft.wms_id} onChange={(id) => patch({ wms_id: id })} />
				</FilterField>
				<FilterField label="Поставщик">
					<FilterSelect options={options.suppliers} allLabel="Все поставщики" value={draft.supplier_id} onChange={(id) => patch({ supplier_id: id })} />
				</FilterField>
				<FilterField label="Категория">
					<FilterSelect options={options.categories} allLabel="Все категории" value={draft.category_id} onChange={(id) => patch({ category_id: id })} />
				</FilterField>
				<FilterField label="Тип изменения">
					<FilterSelect options={directionOptions} value={draft.direction} onChange={(id) => patch({ direction: id })} />
				</FilterField>
				<FilterField label="Закупок в периоде">
					<FilterSegment options={purchasesOptions} value={Number(draft.min_purchases)} onChange={(id) => patch({ min_purchases: id })} />
				</FilterField>
				<FilterField label="Мин. изменение, %">
					<input
						type="number"
						min="0"
						className="form-control"
						style={inputStyle}
						placeholder="любое"
						value={draft.min_change_percent}
						onChange={(event) => patch({ min_change_percent: event.target.value === "" ? "" : Math.max(0, Number(event.target.value)) })}
					/>
				</FilterField>
				<FilterField label="Товар">
					<input className="form-control" style={inputStyle} value={draft.search} onChange={(event) => patch({ search: event.target.value })} placeholder="Поиск по названию" />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row g-3 mb-3">
				<div className="col-xl col-md-4 col-6"><KpiCard title="Пар в отчёте" value={formatMoney(summary.total_items, 0)} sublabel={`товар × поставщик, всего ${formatMoney(summary.pairs_total, 0)}`} icon="uil-box" color="#4c6ef0" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Подорожало" value={formatMoney(summary.increased, 0)} icon="uil-arrow-up" color="#d23434" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Подешевело" value={formatMoney(summary.decreased, 0)} icon="uil-arrow-down" color="#1f9d57" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Без изменений" value={formatMoney(summary.unchanged, 0)} sublabel="2+ закупки по одной цене" icon="uil-minus" color="#6b7280" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Одна закупка" value={formatMoney(summary.single, 0)} sublabel="сравнивать не с чем" icon="uil-circle" color="#6b7280" /></div>
				<div className="col-xl col-md-4 col-6"><KpiCard title="Среднее изменение" value={`${Number(summary.average_change_percent || 0).toFixed(1)}%`} sublabel="по изменившимся парам" icon="uil-chart-line" color="#f59f00" /></div>
			</div>
			<div className="row g-3 mb-3">
				<div className="col-md-6">
					<KpiCard
						title="Переплата"
						value={formatMoney(summary.overpay)}
						sublabel="сколько заплатили сверх первой цены периода: закупки дороже первой × их количество"
						icon="uil-coins"
						color="#d23434"
					/>
				</div>
				<div className="col-md-6">
					<KpiCard
						title="Итог к первой цене"
						value={formatMoney(summary.net_effect)}
						sublabel="переплата минус экономия на закупках дешевле первой цены"
						icon="uil-balance-scale"
						color="#4c6ef0"
					/>
				</div>
			</div>

			<div className="row g-3 mb-3">
				<div className="col-lg-6">
					<TopChangesTable title="Топ-10 подорожаний" items={data?.top_increases || []} emptyText="Нет подорожаний" />
				</div>
				<div className="col-lg-6">
					<TopChangesTable title="Топ-10 снижений" items={data?.top_decreases || []} emptyText="Нет снижений" />
				</div>
			</div>

			<SuppliersTable suppliers={data?.suppliers || []} />
			<ChangesTable key={JSON.stringify(applied)} items={items} onOpenHistory={openHistory} />

			<HistoryModal
				item={historyItem}
				history={history}
				show={!!historyItem}
				onHide={() => { setHistoryItem(null); setHistory([]) }}
			/>
		</>
	)
}

export default PurchasePriceChanges
